import React, { useState, useEffect, useCallback } from "react";
import { useT } from "../i18n/useT";
import {
  boStores, boCreateStore, boUpdateStore,
  boItems, boCreateItem, boUpdateItem,
  boGetPolicy, boSetPolicy,
  boRequisitions, boCreateRequisition, boSubmitRequisition, boApproveRequisition, boRejectRequisition, boCreatePOFromReq,
  boGoodsReceipts, boCreateGRN, boPostGRN,
  boTransfers, boCreateTransfer, boPostTransfer,
  boIssues, boCreateIssue, boPostIssue,
  boStock, boAdjustStock, boDashboard, boValuation, boLowStock,
  fetchSuppliers, fetchPurchaseOrders, getCurrency,
} from "../services/api";

// Back Office (Store & Procurement) — multi-store inventory, requisitions with
// configurable approval, goods receiving, inter-store transfers, issues, and a
// live valuation dashboard. Reuses the Ingredient catalogue as the unified item
// list (item_type distinguishes food / beverage / supply / asset).
const S = {
  en: {
    title: "Back Office", subtitle: "Store & Procurement",
    tab_dashboard: "Dashboard", tab_stores: "Stores", tab_items: "Items", tab_req: "Requisitions",
    tab_receiving: "Receiving", tab_stock: "Stock", tab_transfers: "Transfers", tab_issues: "Issues", tab_settings: "Settings",
    m_pending: "Pending requisitions", m_stores: "Stores", m_items: "Items", m_low: "Low-stock items", m_valuation: "Stock valuation",
    valuation_by_store: "Valuation by store", low_stock: "Low stock", none: "None",
    name: "Name", kind: "Kind", active: "Active", add: "Add", save: "Save", cancel: "Cancel",
    store: "Store", department: "Department", unit: "Unit", category: "Category", type: "Type",
    receipt_type: "Receipt type", with_receipt: "With receipt", without_receipt: "Without receipt",
    unit_cost: "Unit cost", reorder: "Reorder level", par: "Par level", supplier: "Supplier", optional: "optional",
    code: "Code", tax: "Tax", vat_standard: "VAT 15%", vat_exempt: "VAT-exempt", vat_zero: "Zero-rated",
    add_store: "Add store", add_item: "Add item", item: "Item", qty: "Quantity", price: "Unit price",
    lines: "Lines", add_line: "Add line", note: "Note", create: "Create", status: "Status", total: "Total",
    number: "Number", actions: "Actions", submit: "Submit", approve: "Approve", reject: "Reject", make_po: "Create PO", post: "Post",
    new_req: "New requisition", new_grn: "New goods receipt", new_transfer: "New transfer", new_issue: "New issue",
    from_store: "From store", to_store: "To store", on_hand: "On hand", value: "Value", adjust: "Adjust", adjust_stock: "Adjust stock",
    approval_policy: "Approval policy", mode: "Mode", threshold: "Threshold amount", approver: "Approver role", second_approver: "Second approver role",
    mode_none: "No approval (auto-approve)", mode_single: "Single approval", mode_two: "Two-level (by amount)",
    kind_main: "Main", kind_kitchen: "Kitchen", kind_bar: "Bar", kind_housekeeping: "Housekeeping", kind_general: "General",
    t_food: "Food", t_beverage: "Beverage", t_supply: "Supply", t_asset: "Asset",
    all_types: "All types", search: "Search…", loading: "Loading…", empty: "Nothing here yet.", saved: "Saved.",
    deactivate: "Deactivate", activate: "Activate", refresh: "Refresh", filter_store: "Filter by store", all_stores: "All stores",
    receipt: "Goods receipt", from_po: "From purchase order", po_id: "Purchase order ID",
  },
  am: {
    title: "ጀርባ ቢሮ", subtitle: "መጋዘንና ግዥ",
    tab_dashboard: "ዳሽቦርድ", tab_stores: "መጋዘኖች", tab_items: "እቃዎች", tab_req: "የግዥ ጥያቄዎች",
    tab_receiving: "መቀበያ", tab_stock: "ክምችት", tab_transfers: "ዝውውሮች", tab_issues: "መውጫዎች", tab_settings: "ቅንብሮች",
    m_pending: "በመጠባበቅ ላይ ያሉ ጥያቄዎች", m_stores: "መጋዘኖች", m_items: "እቃዎች", m_low: "ዝቅተኛ ክምችት እቃዎች", m_valuation: "የክምችት ግምት",
    valuation_by_store: "በመጋዘን ግምት", low_stock: "ዝቅተኛ ክምችት", none: "የለም",
    name: "ስም", kind: "ዓይነት", active: "ንቁ", add: "ጨምር", save: "አስቀምጥ", cancel: "ተወው",
    store: "መጋዘን", department: "ክፍል", unit: "መለኪያ", category: "ምድብ", type: "ዓይነት",
    receipt_type: "የደረሰኝ ዓይነት", with_receipt: "ከደረሰኝ ጋር", without_receipt: "ያለ ደረሰኝ",
    unit_cost: "የአንዱ ዋጋ", reorder: "እንደገና ማዘዣ ደረጃ", par: "ፓር ደረጃ", supplier: "አቅራቢ", optional: "አማራጭ",
    code: "ኮድ", tax: "ታክስ", vat_standard: "ተ.እ.ታ 15%", vat_exempt: "ከተ.እ.ታ ነፃ", vat_zero: "ዜሮ የተከፈለ",
    add_store: "መጋዘን ጨምር", add_item: "እቃ ጨምር", item: "እቃ", qty: "ብዛት", price: "የአንዱ ዋጋ",
    lines: "መስመሮች", add_line: "መስመር ጨምር", note: "ማስታወሻ", create: "ፍጠር", status: "ሁኔታ", total: "ጠቅላላ",
    number: "ቁጥር", actions: "ድርጊቶች", submit: "አስገባ", approve: "አጽድቅ", reject: "ውድቅ አድርግ", make_po: "ግዥ ትዕዛዝ ፍጠር", post: "አስመዝግብ",
    new_req: "አዲስ የግዥ ጥያቄ", new_grn: "አዲስ መቀበያ", new_transfer: "አዲስ ዝውውር", new_issue: "አዲስ መውጫ",
    from_store: "ከመጋዘን", to_store: "ወደ መጋዘን", on_hand: "በእጅ ያለ", value: "ግምት", adjust: "አስተካክል", adjust_stock: "ክምችት አስተካክል",
    approval_policy: "የማጽደቅ ፖሊሲ", mode: "ሁነታ", threshold: "የገደብ መጠን", approver: "አጽዳቂ ሚና", second_approver: "ሁለተኛ አጽዳቂ ሚና",
    mode_none: "ማጽደቅ የለም (በራስ-ሰር)", mode_single: "ነጠላ ማጽደቅ", mode_two: "ሁለት-ደረጃ (በመጠን)",
    kind_main: "ዋና", kind_kitchen: "ወጥ ቤት", kind_bar: "ባር", kind_housekeeping: "ቤት አያያዝ", kind_general: "አጠቃላይ",
    t_food: "ምግብ", t_beverage: "መጠጥ", t_supply: "አቅርቦት", t_asset: "ንብረት",
    all_types: "ሁሉም ዓይነቶች", search: "ፈልግ…", loading: "በመጫን ላይ…", empty: "እስካሁን ምንም የለም።", saved: "ተቀምጧል።",
    deactivate: "አቦዝን", activate: "አንቃ", refresh: "አድስ", filter_store: "በመጋዘን አጣራ", all_stores: "ሁሉም መጋዘኖች",
    receipt: "መቀበያ", from_po: "ከግዥ ትዕዛዝ", po_id: "የግዥ ትዕዛዝ ቁጥር",
  },
  om: {
    title: "Waajjira Duubaa", subtitle: "Kuusaa fi Bitannaa",
    tab_dashboard: "Daashboordii", tab_stores: "Kuusaalee", tab_items: "Meeshaalee", tab_req: "Gaaffiilee Bitannaa",
    tab_receiving: "Fudhannaa", tab_stock: "Kuusaa", tab_transfers: "Dabarsaalee", tab_issues: "Baʼuu", tab_settings: "Qindaaʼina",
    m_pending: "Gaaffiilee eegaa jiran", m_stores: "Kuusaalee", m_items: "Meeshaalee", m_low: "Meeshaalee kuusaa gadaanaa", m_valuation: "Gatii kuusaa",
    valuation_by_store: "Gatii kuusaadhaan", low_stock: "Kuusaa gadaanaa", none: "Hin jiru",
    name: "Maqaa", kind: "Gosa", active: "Kaʼaa", add: "Dabali", save: "Olkaaʼi", cancel: "Dhiisi",
    store: "Kuusaa", department: "Kutaa", unit: "Safartuu", category: "Ramaddii", type: "Gosa",
    receipt_type: "Gosa nagahee", with_receipt: "Nagahee waliin", without_receipt: "Nagahee malee",
    unit_cost: "Gatii tokkoo", reorder: "Sadarkaa deebiʼanii ajajuu", par: "Sadarkaa paar", supplier: "Dhiyeessaa", optional: "filannoo",
    code: "Koodii", tax: "Gibira", vat_standard: "VAT 15%", vat_exempt: "VAT irraa bilisa", vat_zero: "Zeeroo",
    add_store: "Kuusaa dabali", add_item: "Meeshaa dabali", item: "Meeshaa", qty: "Baayʼina", price: "Gatii tokkoo",
    lines: "Sararoota", add_line: "Sarara dabali", note: "Yaadannoo", create: "Uumi", status: "Haala", total: "Waliigala",
    number: "Lakkoofsa", actions: "Gochaalee", submit: "Galchi", approve: "Mirkaneessi", reject: "Kuffisi", make_po: "Ajaja Bitannaa uumi", post: "Galmeessi",
    new_req: "Gaaffii haaraa", new_grn: "Fudhannaa haaraa", new_transfer: "Dabarsa haaraa", new_issue: "Baʼuu haaraa",
    from_store: "Kuusaa irraa", to_store: "Gara kuusaa", on_hand: "Harka jiru", value: "Gatii", adjust: "Sirreessi", adjust_stock: "Kuusaa sirreessi",
    approval_policy: "Imaammata mirkaneessaa", mode: "Haala", threshold: "Hanga daangaa", approver: "Gahee mirkaneessaa", second_approver: "Gahee mirkaneessaa lammaffaa",
    mode_none: "Mirkaneessa hin jiru (ofumaan)", mode_single: "Mirkaneessa tokko", mode_two: "Sadarkaa-lama (hangaan)",
    kind_main: "Ijoo", kind_kitchen: "Kushiinaa", kind_bar: "Baar", kind_housekeeping: "Eegumsa manaa", kind_general: "Waliigala",
    t_food: "Nyaata", t_beverage: "Dhugaatii", t_supply: "Dhiyeessa", t_asset: "Qabeenya",
    all_types: "Gosoota hunda", search: "Barbaadi…", loading: "Fe'aa jira…", empty: "Amma homaa hin jiru.", saved: "Olkaaʼameera.",
    deactivate: "Dhaamsi", activate: "Kaasi", refresh: "Haaromsi", filter_store: "Kuusaadhaan calalii", all_stores: "Kuusaalee hunda",
    receipt: "Fudhannaa", from_po: "Ajaja bitannaa irraa", po_id: "Lakkoofsa ajaja bitannaa",
  },
};

const C = { border: "1px solid #e2e6ea", green: "#0F6E56", greenBg: "#E7F6EC", muted: "#5b6472", danger: "#b23b3b" };

// Round quantities for display so floating-point math (e.g. 4.7000000000003)
// never leaks to the screen; trims trailing zeros; shows an em dash for null.
const fmtQty = (n) => (n == null ? "—" : String(Number(Number(n).toFixed(3))));

const cardStyle = { background: "#fff", border: C.border, borderRadius: 10, padding: 16, marginBottom: 14 };
const inputStyle = { padding: "7px 10px", borderRadius: 8, border: C.border, fontSize: 13.5, minWidth: 0 };
const btnStyle = { padding: "7px 12px", borderRadius: 8, border: "none", background: C.green, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnGhost = { ...btnStyle, background: "#fff", color: C.green, border: `1px solid ${C.green}` };
const btnSmall = { padding: "4px 9px", borderRadius: 7, border: C.border, background: "#fff", fontSize: 12, cursor: "pointer", marginRight: 5 };
const thStyle = { textAlign: "left", padding: "7px 8px", fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.4, color: C.muted, borderBottom: C.border };
const tdStyle = { padding: "7px 8px", fontSize: 13, borderBottom: "1px solid #f0f2f4" };

function StatusPill({ s }) {
  const map = {
    draft: "#8a94a6", submitted: "#b8860b", approved: C.green, rejected: C.danger,
    ordered: "#2b6cb0", received: C.green, posted: C.green, cancelled: "#8a94a6",
  };
  return <span style={{ background: (map[s] || "#8a94a6") + "22", color: map[s] || "#8a94a6", padding: "2px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 700 }}>{s}</span>;
}

// Reusable line editor: [{ingredient_id, quantity, unit_price?}]
function LineEditor({ items, lines, setLines, t, withPrice }) {
  const [q, setQ] = useState("");
  const shown = q
    ? items.filter((it) => `${it.name} ${it.item_code || ""}`.toLowerCase().includes(q.toLowerCase()))
    : items;
  const add = () => setLines([...lines, { ingredient_id: items[0]?.id || "", quantity: 1, unit_price: 0 }]);
  const upd = (i, k, v) => setLines(lines.map((ln, j) => (j === i ? { ...ln, [k]: v } : ln)));
  const del = (i) => setLines(lines.filter((_, j) => j !== i));
  return (
    <div>
      <input style={{ ...inputStyle, marginBottom: 8, width: "100%", maxWidth: 320 }} placeholder={"🔎 " + t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
      {lines.map((ln, i) => (
        <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap", alignItems: "center" }}>
          <select style={{ ...inputStyle, flex: "2 1 160px" }} value={ln.ingredient_id} onChange={(e) => upd(i, "ingredient_id", Number(e.target.value))}>
            {shown.map((it) => <option key={it.id} value={it.id}>{it.item_code ? it.item_code + " · " : ""}{it.name} ({it.unit})</option>)}
          </select>
          <input style={{ ...inputStyle, flex: "1 1 80px" }} type="number" min="0" step="any" placeholder={t("qty")} value={ln.quantity} onChange={(e) => upd(i, "quantity", Number(e.target.value))} />
          {withPrice && <input style={{ ...inputStyle, flex: "1 1 80px" }} type="number" min="0" step="any" placeholder={t("price")} value={ln.unit_price} onChange={(e) => upd(i, "unit_price", Number(e.target.value))} />}
          <button type="button" style={{ ...btnSmall, color: C.danger }} onClick={() => del(i)}>✕</button>
        </div>
      ))}
      <button type="button" style={btnGhost} onClick={add}>+ {t("add_line")}</button>
    </div>
  );
}

export default function BackOfficePage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency ? getCurrency() : "Br";
  const money = (n) => `${cur} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  const TABS = ["dashboard", "stores", "items", "req", "receiving", "stock", "transfers", "issues", "settings"];
  const hashTab = (typeof window !== "undefined" ? (window.location.hash || "").replace("#", "") : "");
  const [tab, setTabState] = useState(TABS.includes(hashTab) ? hashTab : "dashboard");
  const setTab = (k) => { setTabState(k); if (typeof window !== "undefined") { try { window.history.replaceState(null, "", "#" + k); } catch (_) {} } };
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [msg, setMsg] = useState("");

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2500); };
  const err = (e) => flash(e?.response?.data?.detail || "Error");

  const loadShared = useCallback(async () => {
    try {
      const [st, it] = await Promise.all([boStores(), boItems()]);
      setStores(st); setItems(it);
      try { setSuppliers(await fetchSuppliers()); } catch (_) { /* purchasing may be gated */ }
    } catch (e) { err(e); }
  }, []);
  useEffect(() => { loadShared(); }, [loadShared]);

  const tabs = TABS;
  const tabBtn = (k) => ({
    padding: "7px 13px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: tab === k ? 700 : 500,
    border: tab === k ? `1.5px solid ${C.green}` : C.border, background: tab === k ? C.greenBg : "#fff",
    color: tab === k ? C.green : C.muted, whiteSpace: "nowrap",
  });

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 4px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>{t("title")}</h1>
        <span style={{ color: C.muted, fontSize: 14 }}>{t("subtitle")}</span>
      </div>
      <div style={{ display: "flex", gap: 7, marginTop: 14, flexWrap: "wrap", overflowX: "auto" }}>
        {tabs.map((k) => <button key={k} style={tabBtn(k)} onClick={() => setTab(k)}>{t("tab_" + k)}</button>)}
      </div>
      {msg && <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: C.greenBg, color: C.green, fontSize: 13 }}>{msg}</div>}

      <div style={{ marginTop: 16 }}>
        {tab === "dashboard" && <DashboardTab t={t} money={money} />}
        {tab === "stores" && <StoresTab t={t} stores={stores} reload={loadShared} flash={flash} err={err} />}
        {tab === "items" && <ItemsTab t={t} money={money} suppliers={suppliers} reload={loadShared} flash={flash} err={err} />}
        {tab === "req" && <RequisitionsTab t={t} money={money} stores={stores} items={items} suppliers={suppliers} flash={flash} err={err} />}
        {tab === "receiving" && <ReceivingTab t={t} money={money} stores={stores} items={items} suppliers={suppliers} reload={loadShared} flash={flash} err={err} />}
        {tab === "stock" && <StockTab t={t} money={money} stores={stores} items={items} flash={flash} err={err} />}
        {tab === "transfers" && <TransfersTab t={t} stores={stores} items={items} flash={flash} err={err} />}
        {tab === "issues" && <IssuesTab t={t} stores={stores} items={items} flash={flash} err={err} />}
        {tab === "settings" && <SettingsTab t={t} flash={flash} err={err} />}
      </div>
    </div>
  );
}

function DashboardTab({ t, money }) {
  const [d, setD] = useState(null);
  const [val, setVal] = useState([]);
  const [low, setLow] = useState([]);
  useEffect(() => {
    boDashboard().then(setD).catch(() => {});
    boValuation().then(setVal).catch(() => {});
    boLowStock().then(setLow).catch(() => {});
  }, []);
  if (!d) return <div style={{ color: C.muted }}>{t("loading")}</div>;
  const metrics = [
    [t("m_pending"), d.pending_requisitions], [t("m_stores"), d.stores],
    [t("m_items"), d.items], [t("m_low"), d.low_stock_items], [t("m_valuation"), money(d.stock_valuation)],
  ];
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
        {metrics.map(([label, v]) => (
          <div key={label} style={cardStyle}>
            <div style={{ color: C.muted, fontSize: 12.5 }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: C.green, marginTop: 4 }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>{t("valuation_by_store")}</h3>
          {val.length === 0 ? <div style={{ color: C.muted, fontSize: 13 }}>{t("empty")}</div> :
            val.map((v) => <div key={v.store} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13.5, borderBottom: "1px solid #f0f2f4" }}><span>{v.store}</span><b>{money(v.value)}</b></div>)}
        </div>
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>{t("low_stock")}</h3>
          {low.length === 0 ? <div style={{ color: C.muted, fontSize: 13 }}>{t("none")}</div> :
            low.map((l) => <div key={l.ingredient_id} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13.5, borderBottom: "1px solid #f0f2f4" }}><span>{l.name}</span><span style={{ color: C.danger }}>{fmtQty(l.stock_on_hand)} / {fmtQty(l.reorder_level)} {l.unit}</span></div>)}
        </div>
      </div>
    </div>
  );
}

function StoresTab({ t, stores, reload, flash, err }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("general");
  const kinds = ["main", "kitchen", "bar", "housekeeping", "general"];
  const create = async () => {
    if (!name.trim()) return;
    try { await boCreateStore({ name, kind }); setName(""); flash(t("saved")); reload(); } catch (e) { err(e); }
  };
  const toggle = async (s) => { try { await boUpdateStore(s.id, { active: !s.active }); reload(); } catch (e) { err(e); } };
  return (
    <div>
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>{t("add_store")}</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input style={{ ...inputStyle, flex: "2 1 180px" }} placeholder={t("name")} value={name} onChange={(e) => setName(e.target.value)} />
          <select style={{ ...inputStyle, flex: "1 1 120px" }} value={kind} onChange={(e) => setKind(e.target.value)}>
            {kinds.map((k) => <option key={k} value={k}>{t("kind_" + k)}</option>)}
          </select>
          <button style={btnStyle} onClick={create}>{t("add")}</button>
        </div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", ...cardStyle, padding: 0, overflow: "hidden" }}>
        <thead><tr><th style={thStyle}>{t("name")}</th><th style={thStyle}>{t("kind")}</th><th style={thStyle}>{t("active")}</th><th style={thStyle}>{t("actions")}</th></tr></thead>
        <tbody>
          {stores.map((s) => (
            <tr key={s.id}>
              <td style={tdStyle}>{s.name}{s.is_default ? " ★" : ""}</td>
              <td style={tdStyle}>{t("kind_" + s.kind)}</td>
              <td style={tdStyle}>{s.active ? "✓" : "—"}</td>
              <td style={tdStyle}><button style={btnSmall} onClick={() => toggle(s)}>{s.active ? t("deactivate") : t("activate")}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ItemsTab({ t, money, suppliers, flash, err }) {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [f, setF] = useState({ name: "", unit: "", item_type: "food", category: "", department: "", item_code: "", vat_class: "standard", unit_cost: 0, reorder_level: "", par_level: "", preferred_supplier_id: "" });
  const types = ["food", "beverage", "supply", "asset"];
  const load = useCallback(() => { boItems({ ...(type ? { item_type: type } : {}), ...(q ? { q } : {}) }).then(setRows).catch(() => {}); }, [type, q]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.name.trim() || !f.unit.trim()) return;
    const body = { name: f.name, unit: f.unit, item_type: f.item_type, category: f.category || null,
      department: f.department || null,
      item_code: f.item_code || null, vat_class: f.vat_class || "standard",
      unit_cost: Number(f.unit_cost) || 0,
      reorder_level: f.reorder_level === "" ? null : Number(f.reorder_level),
      par_level: f.par_level === "" ? null : Number(f.par_level),
      preferred_supplier_id: f.preferred_supplier_id ? Number(f.preferred_supplier_id) : null };
    try { await boCreateItem(body); setF({ ...f, name: "", unit: "", category: "", item_code: "", unit_cost: 0, reorder_level: "", par_level: "" }); flash(t("saved")); load(); } catch (e) { err(e); }
  };
  return (
    <div>
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>{t("add_item")}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
          <input style={inputStyle} placeholder={t("name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input style={inputStyle} placeholder={t("unit")} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} />
          <select style={inputStyle} value={f.item_type} onChange={(e) => setF({ ...f, item_type: e.target.value })}>
            {types.map((k) => <option key={k} value={k}>{t("t_" + k)}</option>)}
          </select>
          <input style={inputStyle} placeholder={t("department")} value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} />
          <input style={inputStyle} placeholder={t("category")} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
          <input style={inputStyle} placeholder={t("code")} value={f.item_code} onChange={(e) => setF({ ...f, item_code: e.target.value })} />
          <select style={inputStyle} value={f.vat_class} onChange={(e) => setF({ ...f, vat_class: e.target.value })}>
            <option value="standard">{t("vat_standard")}</option>
            <option value="exempt">{t("vat_exempt")}</option>
            <option value="zero">{t("vat_zero")}</option>
          </select>
          <input style={inputStyle} type="number" step="any" placeholder={t("unit_cost")} value={f.unit_cost} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} />
          <input style={inputStyle} type="number" step="any" placeholder={t("reorder")} value={f.reorder_level} onChange={(e) => setF({ ...f, reorder_level: e.target.value })} />
          <input style={inputStyle} type="number" step="any" placeholder={t("par")} value={f.par_level} onChange={(e) => setF({ ...f, par_level: e.target.value })} />
          <select style={inputStyle} value={f.preferred_supplier_id} onChange={(e) => setF({ ...f, preferred_supplier_id: e.target.value })}>
            <option value="">{t("supplier")} ({t("optional")})</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div style={{ marginTop: 10 }}><button style={btnStyle} onClick={create}>{t("add")}</button></div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <select style={inputStyle} value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">{t("all_types")}</option>
          {types.map((k) => <option key={k} value={k}>{t("t_" + k)}</option>)}
        </select>
        <input style={inputStyle} placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", ...cardStyle, padding: 0, overflow: "hidden" }}>
        <thead><tr><th style={thStyle}>{t("code")}</th><th style={thStyle}>{t("name")}</th><th style={thStyle}>{t("type")}</th><th style={thStyle}>{t("unit")}</th><th style={thStyle}>{t("tax")}</th><th style={thStyle}>{t("unit_cost")}</th><th style={thStyle}>{t("on_hand")}</th><th style={thStyle}>{t("reorder")}</th></tr></thead>
        <tbody>
          {rows.map((i) => (
            <tr key={i.id}>
              <td style={tdStyle}>{i.item_code || "—"}</td><td style={tdStyle}>{i.name}</td><td style={tdStyle}>{t("t_" + i.item_type)}</td><td style={tdStyle}>{i.unit}</td>
              <td style={tdStyle}>{t("vat_" + (i.vat_class || "standard"))}</td><td style={tdStyle}>{money(i.unit_cost)}</td><td style={tdStyle}>{fmtQty(i.stock_on_hand)}</td><td style={tdStyle}>{fmtQty(i.reorder_level)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={tdStyle} colSpan={8}>{t("empty")}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function RequisitionsTab({ t, money, stores, items, flash, err }) {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [store, setStore] = useState("");
  const [dept, setDept] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([]);
  const load = () => boRequisitions().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (lines.length === 0) return;
    try {
      await boCreateRequisition({ store_id: store ? Number(store) : null, department: dept || null, note: note || null,
        lines: lines.map((l) => ({ ingredient_id: l.ingredient_id, quantity: l.quantity, unit_price: l.unit_price || null })) });
      setShow(false); setLines([]); setNote(""); setDept(""); flash(t("saved")); load();
    } catch (e) { err(e); }
  };
  const act = async (fn, id) => { try { await fn(id); load(); } catch (e) { err(e); } };
  return (
    <div>
      <div style={{ marginBottom: 12 }}><button style={btnStyle} onClick={() => setShow(!show)}>{show ? t("cancel") : "+ " + t("new_req")}</button></div>
      {show && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>{t("new_req")}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <select style={inputStyle} value={store} onChange={(e) => setStore(e.target.value)}>
              <option value="">{t("store")}</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input style={inputStyle} placeholder={t("department")} value={dept} onChange={(e) => setDept(e.target.value)} />
            <input style={{ ...inputStyle, flex: "1 1 200px" }} placeholder={t("note")} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>{t("lines")}</div>
          <LineEditor items={items} lines={lines} setLines={setLines} t={t} withPrice />
          <div style={{ marginTop: 12 }}><button style={btnStyle} onClick={create}>{t("create")}</button></div>
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", ...cardStyle, padding: 0, overflow: "hidden" }}>
        <thead><tr><th style={thStyle}>{t("number")}</th><th style={thStyle}>{t("status")}</th><th style={thStyle}>{t("total")}</th><th style={thStyle}>{t("actions")}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={tdStyle}>{r.number}</td>
              <td style={tdStyle}><StatusPill s={r.status} /></td>
              <td style={tdStyle}>{money(r.total)}</td>
              <td style={tdStyle}>
                {r.status === "draft" && <button style={btnSmall} onClick={() => act(boSubmitRequisition, r.id)}>{t("submit")}</button>}
                {r.status === "submitted" && <button style={btnSmall} onClick={() => act((id) => boApproveRequisition(id, ""), r.id)}>{t("approve")}</button>}
                {(r.status === "submitted" || r.status === "draft") && <button style={{ ...btnSmall, color: C.danger }} onClick={() => act((id) => boRejectRequisition(id, ""), r.id)}>{t("reject")}</button>}
                {r.status === "approved" && <button style={btnSmall} onClick={() => act((id) => boCreatePOFromReq(id), r.id)}>{t("make_po")}</button>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={tdStyle} colSpan={4}>{t("empty")}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ReceivingTab({ t, money, stores, items, suppliers, reload, flash, err }) {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [store, setStore] = useState("");
  const [supplier, setSupplier] = useState("");
  const [poId, setPoId] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([]);
  const [pos, setPos] = useState([]);
  const load = () => boGoodsReceipts().then(setRows).catch(() => {});
  useEffect(() => { load(); fetchPurchaseOrders("ordered").then(setPos).catch(() => {}); }, []);
  const create = async () => {
    const body = { store_id: store ? Number(store) : null, supplier_id: supplier ? Number(supplier) : null,
      purchase_order_id: poId ? Number(poId) : null, note: note || null,
      lines: lines.length ? lines.map((l) => ({ ingredient_id: l.ingredient_id, quantity: l.quantity, unit_price: l.unit_price || 0 })) : null };
    try { await boCreateGRN(body); setShow(false); setLines([]); setNote(""); setPoId(""); flash(t("saved")); load(); reload(); } catch (e) { err(e); }
  };
  const post = async (id) => { try { await boPostGRN(id); flash(t("saved")); load(); reload(); } catch (e) { err(e); } };
  return (
    <div>
      <div style={{ marginBottom: 12 }}><button style={btnStyle} onClick={() => setShow(!show)}>{show ? t("cancel") : "+ " + t("new_grn")}</button></div>
      {show && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>{t("new_grn")}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <select style={inputStyle} value={store} onChange={(e) => setStore(e.target.value)}>
              <option value="">{t("store")}</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select style={inputStyle} value={supplier} onChange={(e) => setSupplier(e.target.value)}>
              <option value="">{t("supplier")} ({t("optional")})</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select style={inputStyle} value={poId} onChange={(e) => setPoId(e.target.value)}>
              <option value="">{t("from_po")} ({t("optional")})</option>
              {pos.map((p) => <option key={p.id} value={p.id}>PO #{p.id}{p.supplier_name ? ` · ${p.supplier_name}` : ""} · {money(p.total)}</option>)}
            </select>
            <input style={{ ...inputStyle, flex: "1 1 180px" }} placeholder={t("note")} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>{t("lines")} ({t("from_po")} — {t("optional")})</div>
          <LineEditor items={items} lines={lines} setLines={setLines} t={t} withPrice />
          <div style={{ marginTop: 12 }}><button style={btnStyle} onClick={create}>{t("create")}</button></div>
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", ...cardStyle, padding: 0, overflow: "hidden" }}>
        <thead><tr><th style={thStyle}>{t("number")}</th><th style={thStyle}>{t("status")}</th><th style={thStyle}>{t("actions")}</th></tr></thead>
        <tbody>
          {rows.map((g) => (
            <tr key={g.id}>
              <td style={tdStyle}>{g.number}</td>
              <td style={tdStyle}><StatusPill s={g.status} /></td>
              <td style={tdStyle}>{g.status === "draft" && <button style={btnSmall} onClick={() => post(g.id)}>{t("post")}</button>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={tdStyle} colSpan={3}>{t("empty")}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function StockTab({ t, money, stores, items, flash, err }) {
  const [rows, setRows] = useState([]);
  const [store, setStore] = useState("");
  const [adj, setAdj] = useState({ store_id: "", ingredient_id: "", quantity: "", note: "" });
  const load = useCallback(() => { boStock(store ? { store_id: Number(store) } : {}).then(setRows).catch(() => {}); }, [store]);
  useEffect(() => { load(); }, [load]);
  const doAdjust = async () => {
    if (!adj.store_id || !adj.ingredient_id || !adj.quantity) return;
    try {
      await boAdjustStock({ store_id: Number(adj.store_id), ingredient_id: Number(adj.ingredient_id), quantity: Number(adj.quantity), note: adj.note || null });
      setAdj({ ...adj, quantity: "", note: "" }); flash(t("saved")); load();
    } catch (e) { err(e); }
  };
  return (
    <div>
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>{t("adjust_stock")}</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select style={inputStyle} value={adj.store_id} onChange={(e) => setAdj({ ...adj, store_id: e.target.value })}>
            <option value="">{t("store")}</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select style={inputStyle} value={adj.ingredient_id} onChange={(e) => setAdj({ ...adj, ingredient_id: e.target.value })}>
            <option value="">{t("item")}</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <input style={inputStyle} type="number" step="any" placeholder={"± " + t("qty")} value={adj.quantity} onChange={(e) => setAdj({ ...adj, quantity: e.target.value })} />
          <input style={inputStyle} placeholder={t("note")} value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} />
          <button style={btnStyle} onClick={doAdjust}>{t("adjust")}</button>
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <select style={inputStyle} value={store} onChange={(e) => setStore(e.target.value)}>
          <option value="">{t("all_stores")}</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", ...cardStyle, padding: 0, overflow: "hidden" }}>
        <thead><tr><th style={thStyle}>{t("store")}</th><th style={thStyle}>{t("item")}</th><th style={thStyle}>{t("on_hand")}</th><th style={thStyle}>{t("value")}</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={r.low ? { background: "#fdf3f3" } : {}}>
              <td style={tdStyle}>{r.store_name}</td><td style={tdStyle}>{r.name}{r.low ? " ⚠️" : ""}</td>
              <td style={tdStyle}>{fmtQty(r.quantity)} {r.unit}</td><td style={tdStyle}>{money(r.value)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={tdStyle} colSpan={4}>{t("empty")}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function TransfersTab({ t, stores, items, flash, err }) {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([]);
  const load = () => boTransfers().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!from || !to || lines.length === 0) return;
    try {
      await boCreateTransfer({ from_store_id: Number(from), to_store_id: Number(to), note: note || null,
        lines: lines.map((l) => ({ ingredient_id: l.ingredient_id, quantity: l.quantity })) });
      setShow(false); setLines([]); setNote(""); flash(t("saved")); load();
    } catch (e) { err(e); }
  };
  const post = async (id) => { try { await boPostTransfer(id); flash(t("saved")); load(); } catch (e) { err(e); } };
  return (
    <div>
      <div style={{ marginBottom: 12 }}><button style={btnStyle} onClick={() => setShow(!show)}>{show ? t("cancel") : "+ " + t("new_transfer")}</button></div>
      {show && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>{t("new_transfer")}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <select style={inputStyle} value={from} onChange={(e) => setFrom(e.target.value)}>
              <option value="">{t("from_store")}</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select style={inputStyle} value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">{t("to_store")}</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input style={{ ...inputStyle, flex: "1 1 180px" }} placeholder={t("note")} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>{t("lines")}</div>
          <LineEditor items={items} lines={lines} setLines={setLines} t={t} />
          <div style={{ marginTop: 12 }}><button style={btnStyle} onClick={create}>{t("create")}</button></div>
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", ...cardStyle, padding: 0, overflow: "hidden" }}>
        <thead><tr><th style={thStyle}>{t("number")}</th><th style={thStyle}>{t("status")}</th><th style={thStyle}>{t("actions")}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={tdStyle}>{r.number}</td><td style={tdStyle}><StatusPill s={r.status} /></td>
              <td style={tdStyle}>{r.status === "draft" && <button style={btnSmall} onClick={() => post(r.id)}>{t("post")}</button>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={tdStyle} colSpan={3}>{t("empty")}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function IssuesTab({ t, stores, items, flash, err }) {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [store, setStore] = useState("");
  const [dept, setDept] = useState("");
  const [note, setNote] = useState("");
  const [receiptType, setReceiptType] = useState("");
  const [lines, setLines] = useState([]);
  const load = () => boIssues().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (lines.length === 0) return;
    try {
      await boCreateIssue({ store_id: store ? Number(store) : null, department: dept || null, note: note || null,
        receipt_type: receiptType || null,
        lines: lines.map((l) => ({ ingredient_id: l.ingredient_id, quantity: l.quantity })) });
      setShow(false); setLines([]); setNote(""); setDept(""); setReceiptType(""); flash(t("saved")); load();
    } catch (e) { err(e); }
  };
  const post = async (id) => { try { await boPostIssue(id); flash(t("saved")); load(); } catch (e) { err(e); } };
  return (
    <div>
      <div style={{ marginBottom: 12 }}><button style={btnStyle} onClick={() => setShow(!show)}>{show ? t("cancel") : "+ " + t("new_issue")}</button></div>
      {show && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>{t("new_issue")}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <select style={inputStyle} value={store} onChange={(e) => setStore(e.target.value)}>
              <option value="">{t("store")}</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input style={inputStyle} placeholder={t("department")} value={dept} onChange={(e) => setDept(e.target.value)} />
            <select style={inputStyle} value={receiptType} onChange={(e) => setReceiptType(e.target.value)}>
              <option value="">{t("receipt_type")}</option>
              <option value="with_receipt">{t("with_receipt")}</option>
              <option value="without_receipt">{t("without_receipt")}</option>
            </select>
            <input style={{ ...inputStyle, flex: "1 1 180px" }} placeholder={t("note")} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>{t("lines")}</div>
          <LineEditor items={items} lines={lines} setLines={setLines} t={t} />
          <div style={{ marginTop: 12 }}><button style={btnStyle} onClick={create}>{t("create")}</button></div>
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", ...cardStyle, padding: 0, overflow: "hidden" }}>
        <thead><tr><th style={thStyle}>{t("number")}</th><th style={thStyle}>{t("status")}</th><th style={thStyle}>{t("actions")}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={tdStyle}>{r.number}</td><td style={tdStyle}><StatusPill s={r.status} /></td>
              <td style={tdStyle}>{r.status === "draft" && <button style={btnSmall} onClick={() => post(r.id)}>{t("post")}</button>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={tdStyle} colSpan={3}>{t("empty")}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab({ t, flash, err }) {
  const [p, setP] = useState(null);
  const roles = ["manager", "controller", "store_manager"];
  useEffect(() => { boGetPolicy().then(setP).catch(() => {}); }, []);
  const save = async () => {
    try {
      const saved = await boSetPolicy({ mode: p.mode, threshold_amount: p.threshold_amount === "" ? null : Number(p.threshold_amount),
        approver_role: p.approver_role, second_approver_role: p.second_approver_role });
      setP(saved); flash(t("saved"));
    } catch (e) { err(e); }
  };
  if (!p) return <div style={{ color: C.muted }}>{t("loading")}</div>;
  return (
    <div style={{ ...cardStyle, maxWidth: 460 }}>
      <h3 style={{ marginTop: 0 }}>{t("approval_policy")}</h3>
      <label style={{ display: "block", fontSize: 12.5, color: C.muted, marginBottom: 4 }}>{t("mode")}</label>
      <select style={{ ...inputStyle, width: "100%", marginBottom: 12 }} value={p.mode} onChange={(e) => setP({ ...p, mode: e.target.value })}>
        <option value="none">{t("mode_none")}</option>
        <option value="single">{t("mode_single")}</option>
        <option value="two_level">{t("mode_two")}</option>
      </select>
      {p.mode === "two_level" && (
        <>
          <label style={{ display: "block", fontSize: 12.5, color: C.muted, marginBottom: 4 }}>{t("threshold")}</label>
          <input style={{ ...inputStyle, width: "100%", marginBottom: 12 }} type="number" step="any" value={p.threshold_amount ?? ""} onChange={(e) => setP({ ...p, threshold_amount: e.target.value })} />
        </>
      )}
      {p.mode !== "none" && (
        <>
          <label style={{ display: "block", fontSize: 12.5, color: C.muted, marginBottom: 4 }}>{t("approver")}</label>
          <select style={{ ...inputStyle, width: "100%", marginBottom: 12 }} value={p.approver_role} onChange={(e) => setP({ ...p, approver_role: e.target.value })}>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </>
      )}
      {p.mode === "two_level" && (
        <>
          <label style={{ display: "block", fontSize: 12.5, color: C.muted, marginBottom: 4 }}>{t("second_approver")}</label>
          <select style={{ ...inputStyle, width: "100%", marginBottom: 12 }} value={p.second_approver_role || "manager"} onChange={(e) => setP({ ...p, second_approver_role: e.target.value })}>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </>
      )}
      <button style={btnStyle} onClick={save}>{t("save")}</button>
    </div>
  );
}
